import React, { useState, useCallback } from 'react';
import { ArrowLeft, ShoppingCart, Package } from 'lucide-react';
import './market.css';
import { CartProvider, useCart } from './CartContext';
import MarketHome from './MarketHome';
import StorePage from './StorePage';
import CartPage from './CartPage';
import CheckoutPage from './CheckoutPage';
import OrdersPage from './OrdersPage';
import OrderTrackingPage from './OrderTrackingPage';
import { EventsList, EventDetail, MyTickets } from './EventsPages';
import { ParcelSend, ParcelHistory, ParcelDetail } from './ParcelPages';
import { LodgingsList, LodgingDetail, LodgingBookings } from './LodgingPages';
import { VenuesList, VenueDetail, VenueBookings } from './VenuePages';
import { BeautySalons, BeautySalonDetail, BeautyBookings } from './BeautyPages';
import { RentalAgencies, RentalFleet, RentalBook, RentalsMine } from './RentalPages';
import { RxQuotePage, MyQuotesPage } from './PharmacyPages';
import { marketApi } from './marketApi';
import apiService from '../apiService';

/**
 * Mbiyo Market — module livraison du web client, pleine page.
 * Navigation interne par état (home / store / cart / checkout / orders / order),
 * panier global (CartContext), barre sticky + FAB panier.
 * ⚠ Aucune écriture backend nouvelle : mêmes endpoints que l'app native.
 */
const TITLES = {
  home: 'Mbiyo Market', cart: 'Mon panier', checkout: 'Finaliser la commande',
  orders: 'Mes commandes', order: 'Suivi de commande',
  events: 'Billetterie', event: 'Événement', tickets: 'Mes billets',
  'parcel-send': 'Envoyer un colis', parcels: 'Mes colis', parcel: 'Suivi du colis',
  lodgings: 'Hébergements', lodging: 'Hébergement', 'lodging-bookings': 'Mes réservations',
  venues: 'Salles & espaces', venue: 'Salle', 'venue-bookings': 'Mes réservations de salles',
  beauty: 'Instituts de beauté', 'beauty-salon': 'Institut', 'beauty-bookings': 'Mes rendez-vous',
  rentals: 'Location de véhicules', 'rental-fleet': 'Véhicules', 'rental-book': 'Réserver', 'rentals-mine': 'Mes locations',
  'rx-quote': 'Envoyer mon ordonnance', 'my-quotes': 'Mes devis',
};

// Écran d'un commerce selon son type (lien direct mbiyo.app/b/<supplier_id>).
const routeForSupplier = (s) => {
  if (!s) return { page: 'home' };
  if (s.business_type === 'beauty_salon') return { page: 'beauty-salon', supplierId: s.supplier_id };
  if (s.business_type === 'vehicle_rental') return { page: 'rental-fleet', supplierId: s.supplier_id };
  if (s.business_type === 'hotel') return { page: 'lodgings', supplierId: s.supplier_id }; // ses hébergements (affiné ci-dessous)
  if (s.business_type === 'event_venue') return { page: 'venues', supplierId: s.supplier_id }; // ses salles
  return { page: 'store', supplierId: s.supplier_id };
};

function Shell({ currentUser, isAuthenticated, citySlug, initialSupplierId, hostPage = 'home' }) {
  const { count, subtotal } = useCart();
  const [route, setRoute] = useState(initialSupplierId ? { page: 'store', supplierId: initialSupplierId } : { page: 'home' });

  // Lien direct vers un commerce : ouvrir le bon écran selon son type (boutique, institut, agence…)
  React.useEffect(() => {
    if (!initialSupplierId) return;
    let alive = true;
    marketApi.supplierDetail(initialSupplierId)
      .then(async res => {
        if (!alive || !res?.success || !res.supplier) return;
        let r = routeForSupplier(res.supplier);
        // Hôtel avec un seul hébergement : ouvrir directement sa fiche
        if (res.supplier.business_type === 'hotel') {
          const lr = await apiService.get(`/lodgings?supplier_id=${encodeURIComponent(res.supplier.supplier_id)}&limit=5`).catch(() => null);
          const lodgings = (lr?.lodgings || []).filter(l => l.status === 'active' && l.is_active !== false);
          if (lodgings.length === 1) r = { page: 'lodging', lodgingId: lodgings[0].lodging_id };
        }
        if (!alive) return;
        setRoute(r);
        window.history.replaceState({ page: hostPage, mk: r }, '');
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [initialSupplierId, hostPage]);

  // Navigation portée par l'HISTORIQUE DU NAVIGATEUR : chaque écran du Market
  // est une entrée (state.mk) — le bouton Retour du navigateur et la flèche de
  // la barre font la même chose, et Retour ne quitte plus le site.
  const nav = useCallback((r) => {
    setRoute(r);
    window.history.pushState({ page: hostPage, mk: r }, '');
    window.scrollTo({ top: 0 });
  }, [hostPage]);

  const back = () => window.history.back();

  React.useEffect(() => {
    const onPop = (e) => {
      setRoute(e.state?.mk || { page: 'home' });
      window.scrollTo({ top: 0 });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const title = TITLES[route.page] || 'Mbiyo Market';
  const showFab = count > 0 && ['home', 'store'].includes(route.page);

  return (
    <div className="mk-root">
      {/* Barre du module */}
      <div className="mk-bar">
        <div className="mk-bar-inner">
          {route.page !== 'home' && (
            <button className="mk-iconbtn" onClick={back} aria-label="Retour"><ArrowLeft size={19} /></button>
          )}
          <h1>{title}</h1>
          {isAuthenticated && (
            <button className="mk-iconbtn" onClick={() => nav({ page: 'orders' })} aria-label="Mes commandes">
              <Package size={19} />
            </button>
          )}
          <button className="mk-iconbtn" onClick={() => nav({ page: 'cart' })} aria-label="Panier">
            <ShoppingCart size={19} />
            {count > 0 && <span className="mk-badge">{count > 9 ? '9+' : count}</span>}
          </button>
        </div>
      </div>

      {route.page === 'home' && <MarketHome nav={nav} citySlug={citySlug} isAuthenticated={isAuthenticated} currentUser={currentUser} />}
      {route.page === 'store' && <StorePage supplierId={route.supplierId} nav={nav} />}
      {route.page === 'cart' && <CartPage nav={nav} />}
      {route.page === 'checkout' && (isAuthenticated
        ? <CheckoutPage nav={nav} currentUser={currentUser} citySlug={citySlug} />
        : (
          <div style={{ padding: 16 }}><div style={{ maxWidth: 460, margin: '24px auto', textAlign: 'center', padding: 24, background: 'var(--mb-surface, #fff)', borderRadius: 16, border: '1px solid var(--mb-border, #E5E7EB)' }}>
            <h2 style={{ margin: '0 0 8px' }}>Connectez-vous pour commander</h2>
            <p className="mk-note" style={{ margin: '0 0 16px' }}>Votre panier est conservé. Après la connexion, ouvrez le Market depuis l'accueil pour finaliser la commande.</p>
            <a className="mk-btn primary" href="/?connexion=1">Se connecter ou créer un compte</a>
          </div></div>
        ))}
      {route.page === 'orders' && <OrdersPage nav={nav} />}
      {route.page === 'order' && <OrderTrackingPage orderId={route.orderId} nav={nav} />}

      {route.page === 'events' && <EventsList nav={nav} citySlug={citySlug} />}
      {route.page === 'event' && <EventDetail eventId={route.eventId} nav={nav} currentUser={currentUser} />}
      {route.page === 'tickets' && <MyTickets nav={nav} />}

      {route.page === 'parcel-send' && <ParcelSend nav={nav} />}
      {route.page === 'parcels' && <ParcelHistory nav={nav} />}
      {route.page === 'parcel' && <ParcelDetail parcelId={route.parcelId} nav={nav} />}

      {route.page === 'lodgings' && <LodgingsList nav={nav} citySlug={citySlug} supplierId={route.supplierId} />}
      {route.page === 'lodging' && <LodgingDetail lodgingId={route.lodgingId} initialCheckIn={route.checkIn} initialCheckOut={route.checkOut} initialAdults={route.adults} initialChildren={route.children} nav={nav} />}
      {route.page === 'lodging-bookings' && <LodgingBookings nav={nav} />}

      {route.page === 'venues' && <VenuesList nav={nav} citySlug={citySlug} supplierId={route.supplierId} />}
      {route.page === 'venue' && <VenueDetail venueId={route.venueId} initialDate={route.date} initialSlot={route.slot} nav={nav} currentUser={currentUser} isAuthenticated={isAuthenticated} />}
      {route.page === 'venue-bookings' && <VenueBookings nav={nav} currentUser={currentUser} />}

      {route.page === 'beauty' && <BeautySalons nav={nav} citySlug={citySlug} />}
      {route.page === 'beauty-salon' && <BeautySalonDetail supplierId={route.supplierId} nav={nav} currentUser={currentUser} />}
      {route.page === 'beauty-bookings' && <BeautyBookings nav={nav} />}

      {route.page === 'rentals' && <RentalAgencies nav={nav} citySlug={citySlug} />}
      {route.page === 'rental-fleet' && <RentalFleet supplierId={route.supplierId} nav={nav} />}
      {route.page === 'rental-book' && <RentalBook supplierId={route.supplierId} vehicleId={route.vehicleId} vehicle={route.vehicle} nav={nav} />}
      {route.page === 'rentals-mine' && <RentalsMine nav={nav} currentUser={currentUser} />}

      {route.page === 'rx-quote' && <RxQuotePage supplierId={route.supplierId} supplierName={route.supplierName} nav={nav} isAuthenticated={isAuthenticated} />}
      {route.page === 'my-quotes' && <MyQuotesPage nav={nav} />}

      {/* FAB panier */}
      {showFab && (
        <button className="mk-fab" onClick={() => nav({ page: 'cart' })}>
          <ShoppingCart size={19} />
          <span className="count">{count}</span>
          Voir le panier
          <span className="total">{subtotal.toLocaleString('fr-FR')} FC</span>
        </button>
      )}
    </div>
  );
}

export default function MarketApp(props) {
  return (
    <CartProvider>
      <Shell {...props} />
    </CartProvider>
  );
}
